import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { STATUS_STYLES } from '../constants';

const { FiDollarSign, FiClock, FiAlertTriangle, FiTrendingUp, FiFileText, FiSend, FiX, FiDownload, FiCheck } = FiIcons;

// Load invoices from localStorage
const loadInvoices = () => {
  try {
    const saved = localStorage.getItem('fresh-board-invoices');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Initial static invoice queue with line items
const STATIC_INVOICE_QUEUE = [
  {
    id: 'INV-2024-0001',
    orderId: 'ORD-2024-0156',
    customer: 'Fresh Market Retailers',
    customerAddress: '123 Market Street, Cape Town',
    amount: 45750,
    status: 'pending_generation',
    date: '2024-01-15',
    lineItems: [
      { description: 'Granny Smith Apples (Premium)', quantity: 150, unitPrice: 155.00, total: 23250 },
      { description: 'Navel Oranges (Class A)', quantity: 100, unitPrice: 180.00, total: 18000 },
      { description: 'Iceberg Lettuce (Fresh)', quantity: 50, unitPrice: 90.00, total: 4500 }
    ],
    subtotal: 45750,
    vat: 6862.50,
    grandTotal: 52612.50
  },
  {
    id: 'INV-2024-0002',
    orderId: 'ORD-2024-0157',
    customer: 'City Supermarket Chain',
    customerAddress: '456 Commerce Ave, Johannesburg',
    amount: 78900,
    status: 'ready_to_send',
    date: '2024-01-14',
    lineItems: [
      { description: 'Beef Tomatoes (Vine)', quantity: 200, unitPrice: 250.00, total: 50000 },
      { description: 'Cherry Tomatoes (Punnet)', quantity: 80, unitPrice: 280.00, total: 22400 },
      { description: 'Mixed Herbs Bundle', quantity: 20, unitPrice: 325.00, total: 6500 }
    ],
    subtotal: 78900,
    vat: 11835.00,
    grandTotal: 90735.00
  }
];

const Financial = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [invoiceQueue, setInvoiceQueue] = React.useState(() => {
    const dynamicInvoices = loadInvoices();
    return [...dynamicInvoices, ...STATIC_INVOICE_QUEUE];
  });
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);

  // Refresh invoices from localStorage when tab changes
  React.useEffect(() => {
    if (activeTab === 'invoicing') {
      const dynamicInvoices = loadInvoices();
      setInvoiceQueue([...dynamicInvoices, ...STATIC_INVOICE_QUEUE]);
    }
  }, [activeTab]);

  // Generate invoice - creates full invoice with line items
  const generateInvoice = (invoice) => {
    // For dynamic invoices from delivery, create sample line items
    const fullInvoice = invoice.lineItems ? invoice : {
      ...invoice,
      customerAddress: '789 Green Lane, Durban',
      lineItems: [
        { description: 'Mixed Produce Bundle', quantity: invoice.items || 6, unitPrice: (invoice.amount / (invoice.items || 6)), total: invoice.amount }
      ],
      subtotal: invoice.amount,
      vat: invoice.amount * 0.15,
      grandTotal: invoice.amount * 1.15
    };
    setGeneratedInvoice(fullInvoice);
  };

  // Export invoice as PDF (simulated)
  const exportToPDF = (invoice) => {
    // Create a simple text representation for download
    const invoiceText = `
FRESH PRODUCE CO.
INVOICE

Invoice #: ${invoice.id || 'INV-' + Date.now()}
Date: ${invoice.date}
Order: ${invoice.orderId}

BILL TO:
${invoice.customer}
${invoice.customerAddress || 'Address on file'}

ITEMS:
${(invoice.lineItems || []).map(item =>
  `${item.description} - Qty: ${item.quantity} x N$${item.unitPrice.toFixed(2)} = N$${item.total.toLocaleString()}`
).join('\n')}

SUBTOTAL: N$ ${invoice.subtotal?.toLocaleString() || invoice.amount.toLocaleString()}
VAT (15%): N$ ${invoice.vat?.toLocaleString() || (invoice.amount * 0.15).toLocaleString()}
GRAND TOTAL: N$ ${invoice.grandTotal?.toLocaleString() || (invoice.amount * 1.15).toLocaleString()}

Thank you for your business!
    `.trim();

    // Create and download file
    const blob = new Blob([invoiceText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice-${invoice.orderId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const financialMetrics = {
    dso: 28.5,
    totalRevenue: 1247500,
    overdueAmount: 145600,
    invoicesPending: 23 + invoiceQueue.filter(inv => inv.status === 'pending').length
  };

  const revenueData = [
    { name: 'Jan', revenue: 185000, profit: 45000 },
    { name: 'Feb', revenue: 198000, profit: 52000 },
    { name: 'Mar', revenue: 210000, profit: 58000 },
    { name: 'Apr', revenue: 225000, profit: 62000 },
    { name: 'May', revenue: 240000, profit: 68000 },
    { name: 'Jun', revenue: 255000, profit: 72000 }
  ];

  const categoryData = [
    { name: 'Fruits', value: 45, color: '#84cc16' },
    { name: 'Vegetables', value: 30, color: '#38bdf8' },
    { name: 'Herbs', value: 15, color: '#a78bfa' },
    { name: 'Exotic', value: 10, color: '#fbbf24' }
  ];

  const debtorData = [
    {
      id: 1,
      customer: 'Fresh Market Retailers',
      totalOwed: 45750,
      overdueDays: 15,
      status: 'overdue',
      lastPayment: '2024-01-01',
      creditLimit: 100000
    },
    {
      id: 2,
      customer: 'City Supermarket Chain',
      totalOwed: 78900,
      overdueDays: 0,
      status: 'current',
      lastPayment: '2024-01-12',
      creditLimit: 200000
    },
    {
      id: 3,
      customer: 'Organic Food Co',
      totalOwed: 23400,
      overdueDays: 45,
      status: 'critical',
      lastPayment: '2023-12-15',
      creditLimit: 50000
    }
  ];

  const getStatusColor = (status) => {
    return STATUS_STYLES[status] || STATUS_STYLES.default.base;
  };

  const tabs = [
    { id: 'overview', label: 'Financial Overview' },
    { id: 'debtors', label: 'AR Management', count: debtorData.length },
    { id: 'invoicing', label: 'Invoice Console', count: invoiceQueue.length }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#111916] border border-[#243129] rounded-lg p-3 shadow-lg">
          <p className="text-[#e8f5e9] font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name === 'revenue' ? 'Revenue' : 'Profit'}: N${entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#111916] border border-[#243129] rounded-lg p-3 shadow-lg">
          <p className="text-[#e8f5e9] font-medium">{payload[0].name}: {payload[0].value}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            Financial <span className="text-[#84cc16]">Reports</span>
          </h1>
          <p className="text-[#6b8772] font-mono text-sm mt-1">Revenue tracking and debtor control</p>
        </div>
        <button className="bg-lime-500/10 border border-lime-500/20 text-[#84cc16] px-4 py-2 rounded-lg hover:bg-lime-500/20 transition-colors flex items-center space-x-2">
          <SafeIcon icon={FiFileText} className="w-4 h-4" />
          <span>Generate Report</span>
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#84cc16]/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#6b8772]">Days Sales Outstanding</p>
              <p className="text-2xl font-bold text-[#84cc16] mt-1">{financialMetrics.dso} days</p>
            </div>
            <div className="bg-lime-500/10 border border-lime-500/20 rounded-lg p-3">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-[#84cc16]" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-sky-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#6b8772]">Monthly Revenue</p>
              <p className="text-2xl font-bold text-[#38bdf8] mt-1">N$ {(financialMetrics.totalRevenue / 1000).toFixed(0)}k</p>
            </div>
            <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-[#38bdf8]" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-red-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#6b8772]">Overdue Accounts</p>
              <p className="text-2xl font-bold text-red-400 mt-1">N$ {(financialMetrics.overdueAmount / 1000).toFixed(0)}k</p>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <SafeIcon icon={FiAlertTriangle} className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-violet-500/30 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#6b8772]">Pending Invoices</p>
              <p className="text-2xl font-bold text-violet-400 mt-1">{financialMetrics.invoicesPending}</p>
            </div>
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3">
              <SafeIcon icon={FiFileText} className="w-6 h-6 text-violet-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="bg-[#111916] rounded-xl border border-[#243129]">
        <div className="border-b border-[#243129]">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#84cc16] text-[#84cc16]'
                    : 'border-transparent text-[#6b8772] hover:text-[#a5c4ad]'
                }`}
              >
                <span className="font-medium">{tab.label}</span>
                {tab.count && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === tab.id ? 'bg-lime-500/10 text-[#84cc16]' : 'bg-[#0a0f0d] text-[#6b8772]'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4"
              >
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Revenue & Profit Trend</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#243129" />
                      <XAxis dataKey="name" tick={{ fill: '#6b8772' }} axisLine={{ stroke: '#243129' }} />
                      <YAxis tickFormatter={(value) => `N$${value / 1000}k`} tick={{ fill: '#6b8772' }} axisLine={{ stroke: '#243129' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" fill="#38bdf8" />
                      <Bar dataKey="profit" fill="#84cc16" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>

              {/* Category Breakdown */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4"
              >
                <h3 className="text-lg font-semibold text-[#e8f5e9] mb-4">Revenue by Category</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}%`}
                        labelLine={{ stroke: '#6b8772' }}
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            </div>
          )}

          {activeTab === 'debtors' && (
            <div className="space-y-6">
              {/* AR Aging Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-4">
                  <p className="text-xs font-mono text-lime-400 uppercase tracking-wider">Current</p>
                  <p className="text-xl font-display font-semibold text-lime-400 mt-1">
                    N$ {debtorData.filter(d => d.overdueDays === 0).reduce((sum, d) => sum + d.totalOwed, 0).toLocaleString()}
                  </p>
                  <p className="text-xs font-mono text-[#6b8772] mt-1">
                    {debtorData.filter(d => d.overdueDays === 0).length} account(s)
                  </p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                  <p className="text-xs font-mono text-amber-400 uppercase tracking-wider">7 Days Overdue</p>
                  <p className="text-xl font-display font-semibold text-amber-400 mt-1">
                    N$ {debtorData.filter(d => d.overdueDays > 0 && d.overdueDays <= 7).reduce((sum, d) => sum + d.totalOwed, 0).toLocaleString()}
                  </p>
                  <p className="text-xs font-mono text-[#6b8772] mt-1">
                    {debtorData.filter(d => d.overdueDays > 0 && d.overdueDays <= 7).length} account(s)
                  </p>
                </div>
                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                  <p className="text-xs font-mono text-orange-400 uppercase tracking-wider">14 Days Overdue</p>
                  <p className="text-xl font-display font-semibold text-orange-400 mt-1">
                    N$ {debtorData.filter(d => d.overdueDays > 7 && d.overdueDays <= 14).reduce((sum, d) => sum + d.totalOwed, 0).toLocaleString()}
                  </p>
                  <p className="text-xs font-mono text-[#6b8772] mt-1">
                    {debtorData.filter(d => d.overdueDays > 7 && d.overdueDays <= 14).length} account(s)
                  </p>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-xs font-mono text-red-400 uppercase tracking-wider">30+ Days Overdue</p>
                  <p className="text-xl font-display font-semibold text-red-400 mt-1">
                    N$ {debtorData.filter(d => d.overdueDays > 14).reduce((sum, d) => sum + d.totalOwed, 0).toLocaleString()}
                  </p>
                  <p className="text-xs font-mono text-[#6b8772] mt-1">
                    {debtorData.filter(d => d.overdueDays > 14).length} account(s)
                  </p>
                </div>
              </div>

              {/* Customer List */}
              <div className="space-y-4">
                <h3 className="text-sm font-mono font-medium text-[#a5c4ad]">Account Details</h3>
                {debtorData.map((debtor) => (
                <motion.div
                  key={debtor.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(debtor.status)}`}>
                        {debtor.status.toUpperCase()}
                      </span>
                      <div>
                        <h4 className="font-medium text-[#e8f5e9]">{debtor.customer}</h4>
                        <p className="text-sm text-[#6b8772]">Last Payment: {debtor.lastPayment}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#e8f5e9]">N$ {debtor.totalOwed.toLocaleString()}</p>
                      <p className="text-sm text-[#6b8772]">Credit Limit: N$ {debtor.creditLimit.toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      {debtor.overdueDays > 0 && (
                        <div className="flex items-center space-x-1 text-red-400">
                          <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                          <span className="text-sm font-medium">{debtor.overdueDays} days overdue</span>
                        </div>
                      )}
                      <div className="text-sm text-[#a5c4ad]">
                        Credit Used: {Math.round((debtor.totalOwed / debtor.creditLimit) * 100)}%
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {debtor.status !== 'current' && (
                        <button className="bg-amber-500/10 border border-amber-500/20 text-amber-400 px-3 py-1 rounded text-sm hover:bg-amber-500/20 transition-colors flex items-center space-x-1">
                          <SafeIcon icon={FiSend} className="w-3 h-3" />
                          <span>Send Reminder</span>
                        </button>
                      )}
                      <button className="text-[#84cc16] hover:text-[#a3e635] text-sm font-medium">
                        View Account
                      </button>
                    </div>
                  </div>
                </motion.div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'invoicing' && (
            <div className="space-y-4">
              {invoiceQueue.map((invoice) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 hover:border-[#243129] transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(invoice.status)}`}>
                        {invoice.status.replace('_', ' ').toUpperCase()}
                      </span>
                      <div>
                        <h4 className="font-medium text-[#e8f5e9]">{invoice.orderId}</h4>
                        <p className="text-sm text-[#6b8772]">{invoice.customer}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-[#e8f5e9]">N$ {invoice.amount.toLocaleString()}</p>
                      <p className="text-sm text-[#6b8772]">Date: {invoice.date || invoice.deliveryDate}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-[#a5c4ad]">
                      {invoice.status === 'pending' ? 'New invoice from delivery' : invoice.status === 'pending_generation' ? 'Ready for invoice generation' : 'Invoice ready to send'}
                    </div>
                    <button
                      onClick={() => generateInvoice(invoice)}
                      className="bg-lime-500/10 border border-lime-500/20 text-[#84cc16] px-4 py-2 rounded-lg hover:bg-lime-500/20 transition-colors flex items-center space-x-2"
                    >
                      <SafeIcon icon={invoice.status === 'pending_generation' || invoice.status === 'pending' ? FiFileText : FiSend} className="w-4 h-4" />
                      <span>{invoice.status === 'pending_generation' || invoice.status === 'pending' ? 'Generate Invoice' : 'View Invoice'}</span>
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Generated Invoice Modal */}
      <AnimatePresence>
        {generatedInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setGeneratedInvoice(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-[#111916] border border-[#243129] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Invoice Header */}
              <div className="flex items-center justify-between p-5 border-b border-[#243129]">
                <div>
                  <h2 className="text-xl font-display font-semibold text-lime-400">INVOICE</h2>
                  <p className="text-sm font-mono text-[#6b8772] mt-1">{generatedInvoice.id || `INV-${Date.now()}`}</p>
                </div>
                <button
                  onClick={() => setGeneratedInvoice(null)}
                  className="text-[#6b8772] hover:text-[#e8f5e9] p-2 hover:bg-[#1c2822] rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {/* Invoice Content */}
              <div className="p-5 space-y-5">
                {/* Company Info */}
                <div className="text-center pb-4 border-b border-[#243129]">
                  <h3 className="text-lg font-display font-semibold text-lime-400">Fresh Produce Co.</h3>
                  <p className="text-sm font-mono text-[#6b8772]">Premium Fresh Produce Suppliers</p>
                </div>

                {/* Invoice Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-3">
                    <p className="text-xs font-mono text-[#6b8772] uppercase">Invoice Date</p>
                    <p className="text-sm font-mono text-[#e8f5e9]">{generatedInvoice.date}</p>
                  </div>
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-3">
                    <p className="text-xs font-mono text-[#6b8772] uppercase">Order Reference</p>
                    <p className="text-sm font-mono text-[#e8f5e9]">{generatedInvoice.orderId}</p>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4">
                  <p className="text-xs font-mono text-[#6b8772] uppercase mb-2">Bill To</p>
                  <p className="text-sm font-medium text-[#e8f5e9]">{generatedInvoice.customer}</p>
                  <p className="text-sm text-[#a5c4ad]">{generatedInvoice.customerAddress}</p>
                </div>

                {/* Line Items */}
                <div>
                  <p className="text-xs font-mono text-[#6b8772] uppercase mb-3">Items</p>
                  <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[#1c2822]">
                        <tr>
                          <th className="text-left px-4 py-2 font-mono text-[#6b8772]">Description</th>
                          <th className="text-right px-4 py-2 font-mono text-[#6b8772]">Qty</th>
                          <th className="text-right px-4 py-2 font-mono text-[#6b8772]">Price</th>
                          <th className="text-right px-4 py-2 font-mono text-[#6b8772]">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(generatedInvoice.lineItems || []).map((item, idx) => (
                          <tr key={idx} className="border-t border-[#1c2822]">
                            <td className="px-4 py-3 text-[#e8f5e9]">{item.description}</td>
                            <td className="px-4 py-3 text-right text-[#a5c4ad]">{item.quantity}</td>
                            <td className="px-4 py-3 text-right text-[#a5c4ad]">N$ {item.unitPrice.toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-[#e8f5e9]">N$ {item.total.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Totals */}
                <div className="bg-[#0a0f0d] border border-[#1c2822] rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6b8772]">Subtotal:</span>
                    <span className="text-[#e8f5e9]">N$ {generatedInvoice.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6b8772]">VAT (15%):</span>
                    <span className="text-[#e8f5e9]">N$ {generatedInvoice.vat?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-[#243129]">
                    <span className="text-[#a5c4ad]">Grand Total:</span>
                    <span className="text-lime-400">N$ {generatedInvoice.grandTotal?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Invoice Actions */}
              <div className="p-5 border-t border-[#243129] flex items-center gap-3">
                <button
                  onClick={() => setGeneratedInvoice(null)}
                  className="flex-1 bg-[#1c2822] border border-[#243129] text-[#a5c4ad] px-4 py-3 rounded-lg hover:border-[#84cc16]/30 transition-colors font-mono text-sm font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => exportToPDF(generatedInvoice)}
                  className="flex-1 bg-gradient-to-r from-lime-500 to-lime-600 text-[#050807] px-4 py-3 rounded-lg shadow-lg shadow-lime-500/20 hover:shadow-lime-500/30 transition-shadow font-mono text-sm font-medium flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  Export PDF
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Financial;
